import { getValidAccessToken, getValidToken } from "./auth";
import {
  type AgentProvider,
  type B2bKeywords,
  type CallDetail,
  type CallsPage,
  type DecisionKeywordSet,
  type DecisionKeywordsView,
  type ElevenLabsVoice,
  type EmmyFacility,
  type EmmyLookupItem,
  type EmmyTicketType,
  type EmmyWiring,
  type MeInfo,
  type OpeningHoursBlock,
  type Practice,
  type PracticeAgentPatch,
  type PracticeAgentSettings,
  type PracticeFull,
  type PracticeLanguage,
  type PracticeLanguageUpsert,
  type PracticeSummary,
  type PracticeUpsert,
  type Prompt,
  type RenderPromptsResponse,
  type RequestType,
  type SettingsChangeRecord,
  type SettingsChangeSummary,
  type SettingsChatResponse,
  type SettingsChatThread,
  type SettingsChatTranscript,
  type SyncClosuresResponse,
  type SystemPrompt,
  type TransferWindow,
  type UnprocessedEvent,
  type UnprocessedEventState,
  type UnprocessedEventsPage,
  type VacationBlock,
} from "./types";

/** Which configuration a settings conversation is about (V025).
 *
 *  One surgery's settings, or the configuration every practice shares — the phrases they all speak and the fleet-wide
 *  keyword lists. The same conversation, proposal and change-log endpoints serve both, differing only in their base
 *  path, so the chat panel takes one of these rather than a practice id. */
export type ChatScopeRef =
  | { kind: "practice"; practiceId: string }
  | { kind: "admin" };

/** Query-key fragment, so two scopes' caches cannot collide. */
export const scopeKey = (scope: ChatScopeRef): string =>
  scope.kind === "admin" ? "admin" : `practice:${scope.practiceId}`;

/** Resolve the API base URL — forgiving of whether `VITE_API_URL` already includes the `/api` suffix.
 *
 *  Accepted inputs:
 *    (unset)                        → "/api"  (relative; assumes dev proxy)
 *    "https://api.example.com"      → "https://api.example.com/api"
 *    "https://api.example.com/"     → "https://api.example.com/api"
 *    "https://api.example.com/api"  → "https://api.example.com/api"
 *    "https://api.example.com/api/" → "https://api.example.com/api"
 */
const resolveApiBase = (raw: string | undefined): string => {
  if (!raw) return "/api";
  const trimmed = raw.replace(/\/+$/, "");
  return trimmed.endsWith("/api") ? trimmed : `${trimmed}/api`;
};

const API_BASE = resolveApiBase(import.meta.env.VITE_API_URL);

/** Where a scope's settings-assistant endpoints live. The two differ only in this prefix, which is what lets one
 *  panel serve both. */
const scopeBase = (scope: ChatScopeRef): string =>
  scope.kind === "admin"
    ? `${API_BASE}/admin`
    : `${API_BASE}/practices/${scope.practiceId}`;

let onAuthExpired: (() => void) | undefined;
export const setOnAuthExpired = (cb: () => void) => {
  onAuthExpired = cb;
};

let practiceIdProvider: (() => string | null) | undefined;
export const setPracticeIdProvider = (p: () => string | null) => {
  practiceIdProvider = p;
};

export class AuthExpiredError extends Error {
  constructor() {
    super("Session expired — please log in again");
    this.name = "AuthExpiredError";
  }
}

export const authHeaders = async (): Promise<Record<string, string>> => {
  // Fetch both tokens in parallel. ID token is for our own verifier (carries user claims); access token is
  // piggy-backed for any endpoint that proxies to Emmy (Emmy's bearerAuth wants the AccessToken).
  const [token, accessToken] = await Promise.all([
    getValidToken(),
    getValidAccessToken(),
  ]);
  const headers: Record<string, string> = token
    ? { Authorization: `Bearer ${token}` }
    : {};
  if (accessToken) headers["X-Emmy-Token"] = accessToken;
  const practiceId = practiceIdProvider?.();
  if (practiceId) headers["X-Practice-Id"] = practiceId;
  return headers;
};

const fetchJson = async <T>(url: string, options?: RequestInit): Promise<T> => {
  const auth = await authHeaders();
  const response = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...auth,
      ...options?.headers,
    },
  });
  if (response.status === 401) {
    onAuthExpired?.();
    throw new AuthExpiredError();
  }
  if (response.status === 204) {
    // 204 No Content — nothing to parse. Return undefined cast to T; callers for 204 endpoints type as `void`.
    return undefined as T;
  }
  if (!response.ok) {
    let msg = `HTTP ${response.status}`;
    try {
      const body = await response.json();
      if (body?.error) msg = String(body.error);
      // Upstream failures carry the detail that actually identifies the problem, and showing only the code turned a
      // specific Emmy error into an opaque "emmy_upstream_error" on screen. Append whatever the API knows.
      const detail = [
        body?.service ? String(body.service).split(".").pop() : null,
        body?.status ? `HTTP ${body.status}` : null,
        body?.message ? String(body.message).slice(0, 200) : null,
      ]
        .filter(Boolean)
        .join(" · ");
      if (detail) msg = `${msg} (${detail})`;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return response.json();
};

export const api = {
  health: () => fetchJson<{ status: string }>(`${API_BASE}/health`),
  me: () => fetchJson<MeInfo>(`${API_BASE}/me`),
  myFacilities: () =>
    fetchJson<{ id: string; name: string }[]>(`${API_BASE}/me/facilities`),
  listPractices: () => fetchJson<Practice[]>(`${API_BASE}/practices`),
  getPractice: (id: string) =>
    fetchJson<PracticeFull>(`${API_BASE}/practices/${id}`),
  createPractice: (p: { id: string } & PracticeUpsert) =>
    fetchJson<Practice>(`${API_BASE}/practices`, {
      method: "POST",
      body: JSON.stringify(p),
    }),
  updatePractice: (id: string, p: PracticeUpsert) =>
    fetchJson<Practice>(`${API_BASE}/practices/${id}`, {
      method: "PUT",
      body: JSON.stringify(p),
    }),
  deletePractice: (id: string) =>
    fetchJson<void>(`${API_BASE}/practices/${id}`, { method: "DELETE" }),
  /** Replace one language's prompt texts. The language is in the path, never inferred: an editor showing Hungarian
   *  must not be able to submit a payload that deletes Czech. */
  updatePrompts: (id: string, language: string, prompts: Prompt[]) =>
    fetchJson<Prompt[]>(
      `${API_BASE}/practices/${id}/prompts/${encodeURIComponent(language)}`,
      {
        method: "PUT",
        body: JSON.stringify(prompts),
      },
    ),
  getPromptAudioUrl: (id: string, language: string, promptId: string) =>
    fetchJson<{ url: string }>(
      `${API_BASE}/practices/${id}/prompts/${encodeURIComponent(language)}/${encodeURIComponent(promptId)}/audio-url`,
    ),
  listLanguages: (id: string) =>
    fetchJson<PracticeLanguage[]>(`${API_BASE}/practices/${id}/languages`),
  /** Adds the language, seeds its prompts from the main language, machine-translates them and queues a render. The
   *  language is NOT offered to callers until it is enabled. */
  addLanguage: (id: string, payload: PracticeLanguageUpsert) =>
    fetchJson<{
      language: PracticeLanguage;
      translated: number;
      translationError?: string | null;
    }>(`${API_BASE}/practices/${id}/languages`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  updateLanguage: (
    id: string,
    language: string,
    payload: PracticeLanguageUpsert,
  ) =>
    fetchJson<PracticeLanguage>(
      `${API_BASE}/practices/${id}/languages/${encodeURIComponent(language)}`,
      { method: "PUT", body: JSON.stringify(payload) },
    ),
  deleteLanguage: (id: string, language: string) =>
    fetchJson<{ deleted: boolean }>(
      `${API_BASE}/practices/${id}/languages/${encodeURIComponent(language)}`,
      { method: "DELETE" },
    ),
  /** Re-run machine translation. Lines an admin has already corrected survive, so this is safe to press again after
   *  editing the main language. */
  translateLanguage: (id: string, language: string) =>
    fetchJson<{ translated: number }>(
      `${API_BASE}/practices/${id}/languages/${encodeURIComponent(language)}/translate`,
      { method: "POST" },
    ),
  setLanguageMenuEnabled: (id: string, enabled: boolean) =>
    fetchJson<{ languageMenuEnabled: boolean }>(
      `${API_BASE}/practices/${id}/language-menu`,
      { method: "POST", body: JSON.stringify({ enabled }) },
    ),
  /** Arm or disarm "press 8 for the recorded information" (V023). Republishes the manifest server-side, so the
   *  caller should poll the practice until the re-render lands, as the language-menu toggle does. */
  setInfoMessageEnabled: (id: string, enabled: boolean) =>
    fetchJson<{ infoMessageEnabled: boolean }>(
      `${API_BASE}/practices/${id}/info-message`,
      { method: "POST", body: JSON.stringify({ enabled }) },
    ),
  updateRequestTypes: (id: string, types: RequestType[]) =>
    fetchJson<RequestType[]>(`${API_BASE}/practices/${id}/request-types`, {
      method: "PUT",
      body: JSON.stringify(types),
    }),
  updateAgent: (id: string, patch: PracticeAgentPatch) =>
    fetchJson<PracticeAgentSettings>(`${API_BASE}/practices/${id}/agent`, {
      method: "PUT",
      body: JSON.stringify(patch),
    }),
  agentProviders: () =>
    fetchJson<AgentProvider[]>(`${API_BASE}/agent/providers`),
  updateTransferWindows: (id: string, windows: TransferWindow[]) =>
    fetchJson<TransferWindow[]>(
      `${API_BASE}/practices/${id}/transfer-windows`,
      {
        method: "PUT",
        body: JSON.stringify(windows),
      },
    ),
  updateB2bKeywords: (id: string, lists: B2bKeywords[]) =>
    fetchJson<B2bKeywords[]>(`${API_BASE}/practices/${id}/b2b-keywords`, {
      method: "PUT",
      body: JSON.stringify(lists),
    }),
  updateOpeningHours: (id: string, hours: OpeningHoursBlock[]) =>
    fetchJson<OpeningHoursBlock[]>(
      `${API_BASE}/practices/${id}/opening-hours`,
      {
        method: "PUT",
        body: JSON.stringify(hours),
      },
    ),

  // --- Vacation blocks (PRD-007 + V007 per-block audio) ---
  listVacationBlocks: (id: string) =>
    fetchJson<VacationBlock[]>(`${API_BASE}/practices/${id}/vacation-blocks`),
  addVacationBlock: (
    id: string,
    startsAt: string,
    endsAt: string,
    czechText: string,
  ) =>
    fetchJson<VacationBlock>(`${API_BASE}/practices/${id}/vacation-blocks`, {
      method: "POST",
      body: JSON.stringify({ startsAt, endsAt, czechText }),
    }),
  updateVacationBlock: (
    id: string,
    blockId: string,
    patch: { startsAt?: string; endsAt?: string; czechText?: string },
  ) =>
    fetchJson<VacationBlock>(
      `${API_BASE}/practices/${id}/vacation-blocks/${encodeURIComponent(blockId)}`,
      { method: "PUT", body: JSON.stringify(patch) },
    ),
  getVacationBlockAudioUrl: (id: string, blockId: string) =>
    fetchJson<{ url: string }>(
      `${API_BASE}/practices/${id}/vacation-blocks/${encodeURIComponent(blockId)}/audio-url`,
    ),
  deleteVacationBlock: (id: string, blockId: string) =>
    fetchJson<void>(
      `${API_BASE}/practices/${id}/vacation-blocks/${encodeURIComponent(blockId)}`,
      {
        method: "DELETE",
      },
    ),

  // --- Emmy credentials + wiring (PRD-007) ---
  getEmmyCredsStatus: (id: string) =>
    fetchJson<{ storedAt: string | null }>(
      `${API_BASE}/practices/${id}/emmy-credentials`,
    ),
  putEmmyCreds: (id: string, username: string, password: string) =>
    fetchJson<{ storedAt: string }>(
      `${API_BASE}/practices/${id}/emmy-credentials`,
      {
        method: "POST",
        body: JSON.stringify({ username, password }),
      },
    ),
  deleteEmmyCreds: (id: string) =>
    fetchJson<void>(`${API_BASE}/practices/${id}/emmy-credentials`, {
      method: "DELETE",
    }),
  listEmmyFacilities: (id: string) =>
    fetchJson<EmmyFacility[]>(`${API_BASE}/practices/${id}/emmy/facilities`),
  listEmmyTicketTypes: (id: string, facilityId: string) =>
    fetchJson<EmmyTicketType[]>(
      `${API_BASE}/practices/${id}/emmy/ticket-types?facilityId=${encodeURIComponent(facilityId)}`,
    ),
  createEmmyTicketType: (
    id: string,
    body: {
      facilityId: string;
      name: string;
      internalName?: string;
      shortName?: string;
      patientCategory: string;
      defaultTriage: string;
      defaultCategory?: string;
      defaultTicketAssignee?: string;
    },
  ) =>
    fetchJson<EmmyTicketType>(`${API_BASE}/practices/${id}/emmy/ticket-types`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  listEmmyTicketCategories: (id: string) =>
    fetchJson<EmmyLookupItem[]>(
      `${API_BASE}/practices/${id}/emmy/ticket-categories`,
    ),
  listEmmyTicketTypeCategories: (id: string) =>
    fetchJson<EmmyLookupItem[]>(
      `${API_BASE}/practices/${id}/emmy/ticket-type-categories`,
    ),
  listEmmyTriages: (id: string) =>
    fetchJson<EmmyLookupItem[]>(`${API_BASE}/practices/${id}/emmy/triages`),
  listEmmyTicketAssignees: (id: string) =>
    fetchJson<EmmyLookupItem[]>(
      `${API_BASE}/practices/${id}/emmy/ticket-assignees`,
    ),
  saveEmmyWiring: (
    id: string,
    facilityId: string,
    ticketTypeIdByTypeId: Record<string, string>,
  ) =>
    fetchJson<EmmyWiring>(`${API_BASE}/practices/${id}/emmy-wiring`, {
      method: "PUT",
      body: JSON.stringify({ facilityId, ticketTypeIdByTypeId }),
    }),
  syncEmmyClosures: (id: string) =>
    fetchJson<SyncClosuresResponse>(
      `${API_BASE}/practices/${id}/emmy/sync-closures`,
      {
        method: "POST",
      },
    ),

  togglePractice: (id: string, enabled: boolean) =>
    fetchJson<Practice>(`${API_BASE}/practices/${id}/toggle`, {
      method: "POST",
      body: JSON.stringify({ enabled }),
    }),
  /** `force` bypasses the per-prompt hash check. It rides in the query string because the handler reads it there —
   *  it used to be sent in the body and silently ignored, so "force re-render" returned 202 and skipped everything. */
  renderPrompts: (id: string, force = false) =>
    fetchJson<RenderPromptsResponse>(
      `${API_BASE}/practices/${id}/render-prompts${force ? "?force=true" : ""}`,
      { method: "POST" },
    ),
  /** Whether Emmy will accept an audio attachment for this practice. False means every call's recording is silently
   *  rejected: Emmy's CreateTicketAttachment requires SecureAttachmentsModule and returns a 400 with no message. */
  getEmmyAttachmentsStatus: (id: string) =>
    fetchJson<{ enabled: boolean; moduleId: string | null }>(
      `${API_BASE}/practices/${id}/emmy/attachments`,
    ),
  enableEmmyAttachments: (id: string) =>
    fetchJson<{ enabled: boolean; created: boolean }>(
      `${API_BASE}/practices/${id}/emmy/attachments`,
      { method: "POST" },
    ),
  listSystemPrompts: () =>
    fetchJson<SystemPrompt[]>(`${API_BASE}/system-prompts`),
  /** V024 decision keywords: the editable word lists, plus the fixed logic around them and which languages are
   *  actually live on calls. Admin-only — one edit changes call handling for every practice at once. */
  listDecisionKeywords: () =>
    fetchJson<DecisionKeywordsView>(`${API_BASE}/decision-keywords`),
  updateDecisionKeywords: (language: string, kind: string, tokens: string[]) =>
    fetchJson<{ set: DecisionKeywordSet; warnings: string[] }>(
      `${API_BASE}/decision-keywords/${encodeURIComponent(language)}/${encodeURIComponent(kind)}`,
      { method: "PUT", body: JSON.stringify({ tokens }) },
    ),
  resetDecisionKeywords: (language: string, kind: string) =>
    fetchJson<{ set: DecisionKeywordSet }>(
      `${API_BASE}/decision-keywords/${encodeURIComponent(language)}/${encodeURIComponent(kind)}/reset`,
      { method: "POST" },
    ),
  updateSystemPrompt: (promptId: string, language: string, czechText: string) =>
    fetchJson<SystemPrompt>(
      `${API_BASE}/system-prompts/${encodeURIComponent(promptId)}`,
      {
        method: "PUT",
        body: JSON.stringify({ czechText, language }),
      },
    ),
  /** Machine-translate the shared system prompts from Czech into one language, overwriting whatever is stored.
   *  Explicit and admin-triggered: unlike per-practice prompts there is no flag protecting a hand-corrected line. */
  translateSystemPrompts: (language: string) =>
    fetchJson<{ language: string; translated: number }>(
      `${API_BASE}/system-prompts/translate`,
      { method: "POST", body: JSON.stringify({ language }) },
    ),
  renderAllPractices: () =>
    fetchJson<{
      queued: string[];
      failed: { practiceId: string; error: string }[];
    }>(`${API_BASE}/system-prompts/render-all`, { method: "POST" }),
  listElevenLabsVoices: () =>
    fetchJson<ElevenLabsVoice[]>(`${API_BASE}/elevenlabs/voices`),
  listCalls: (
    practiceId: string | null,
    fromIso: string,
    toIso: string,
    limit: number,
    offset: number,
  ) => {
    const params = new URLSearchParams();
    if (practiceId) params.set("practiceId", practiceId);
    params.set("from", fromIso);
    params.set("to", toIso);
    params.set("limit", String(limit));
    params.set("offset", String(offset));
    return fetchJson<CallsPage>(`${API_BASE}/calls?${params.toString()}`);
  },
  /** Calls with an unresolved Emmy write problem, of any date. Admin-only. */
  listOpenProblemCalls: (
    practiceId: string | null,
    limit: number,
    offset: number,
  ) => {
    const params = new URLSearchParams();
    if (practiceId) params.set("practiceId", practiceId);
    params.set("problems", "open");
    params.set("limit", String(limit));
    params.set("offset", String(offset));
    return fetchJson<CallsPage>(`${API_BASE}/calls?${params.toString()}`);
  },
  countOpenProblems: (practiceId: string | null) => {
    const params = new URLSearchParams();
    if (practiceId) params.set("practiceId", practiceId);
    return fetchJson<{ open: number }>(
      `${API_BASE}/calls/problems/count?${params.toString()}`,
    );
  },
  resolveCallProblem: (id: string, execution: string) =>
    fetchJson<{ resolved: boolean }>(
      `${API_BASE}/calls/${id}/problem/resolve`,
      {
        method: "POST",
        body: JSON.stringify({ execution }),
      },
    ),
  /** Call events a consumer did not process. Admin-only; `limit = 0` answers only the total. */
  listUnprocessedEvents: (
    state: UnprocessedEventState,
    limit: number,
    offset: number,
  ) => {
    const params = new URLSearchParams();
    params.set("state", state);
    params.set("limit", String(limit));
    params.set("offset", String(offset));
    return fetchJson<UnprocessedEventsPage>(
      `${API_BASE}/unprocessed-events?${params.toString()}`,
    );
  },
  retryUnprocessedEvent: (id: string, generation: number | null) =>
    fetchJson<UnprocessedEvent>(
      `${API_BASE}/unprocessed-events/${encodeURIComponent(id)}/retry`,
      {
        method: "POST",
        body: JSON.stringify({ generation }),
      },
    ),
  resolveUnprocessedEvent: (id: string, generation: number | null) =>
    fetchJson<UnprocessedEvent>(
      `${API_BASE}/unprocessed-events/${encodeURIComponent(id)}/resolve`,
      {
        method: "POST",
        body: JSON.stringify({ generation }),
      },
    ),
  /** Returns the URL only — caller assigns to `<a href>` to trigger the browser's Save-As dialog. The
   *  endpoint streams `Content-Disposition: attachment` so the file lands on disk. */
  callsCsvUrl: (
    practiceId: string | null,
    fromIso: string,
    toIso: string,
  ): string => {
    const params = new URLSearchParams();
    if (practiceId) params.set("practiceId", practiceId);
    params.set("from", fromIso);
    params.set("to", toIso);
    return `${API_BASE}/calls.csv?${params.toString()}`;
  },
  // --- Souhrn: summary + settings assistant (V022) ---

  /** Slower than every other GET here on the first load after a settings change: the API notices the stored text no
   *  longer describes the settings and regenerates it before answering. Subsequent loads are a plain read. */
  getPracticeSummary: (id: string) =>
    fetchJson<PracticeSummary>(`${API_BASE}/practices/${id}/summary`),
  /** One turn. Omit `threadId` to start a conversation; the response carries the one to send next time. The history
   *  is not sent — the API holds it, which is what keeps the transcript and the model's context the same rows. */
  settingsChat: (scope: ChatScopeRef, message: string, threadId?: string) =>
    fetchJson<SettingsChatResponse>(`${scopeBase(scope)}/settings-chat`, {
      method: "POST",
      body: JSON.stringify({ message, threadId }),
    }),
  /** Commit a proposal. Names the message it is stored on rather than carrying it, so what gets written is
   *  necessarily what was shown and agreed to. */
  applySettingsProposal: (
    scope: ChatScopeRef,
    threadId: string,
    messageId: string,
  ) =>
    fetchJson<{ changeId: string; summary: string }>(
      `${scopeBase(scope)}/settings-chat/apply`,
      { method: "POST", body: JSON.stringify({ threadId, messageId }) },
    ),
  listSettingsChatThreads: (scope: ChatScopeRef) =>
    fetchJson<SettingsChatThread[]>(
      `${scopeBase(scope)}/settings-chat/threads`,
    ),
  getSettingsChatTranscript: (scope: ChatScopeRef, threadId: string) =>
    fetchJson<SettingsChatTranscript>(
      `${scopeBase(scope)}/settings-chat/threads/${encodeURIComponent(threadId)}`,
    ),
  listSettingsChanges: (scope: ChatScopeRef) =>
    fetchJson<SettingsChangeSummary[]>(`${scopeBase(scope)}/settings-changes`),
  getSettingsChange: (scope: ChatScopeRef, changeId: string) =>
    fetchJson<SettingsChangeRecord>(
      `${scopeBase(scope)}/settings-changes/${encodeURIComponent(changeId)}`,
    ),
  /** The plain-language description of the configuration every practice shares (V025). Admin-only. */
  getAdminSummary: () =>
    fetchJson<PracticeSummary>(`${API_BASE}/admin/summary`),

  getCall: (id: string) => fetchJson<CallDetail>(`${API_BASE}/calls/${id}`),
  getCallAudioUrl: (id: string) =>
    fetchJson<{ url: string }>(`${API_BASE}/calls/${id}/audio-url`),
  getCallTranscriptUrl: (id: string) =>
    fetchJson<{ url: string }>(`${API_BASE}/calls/${id}/transcript-url`),
  replayCall: (id: string) =>
    fetchJson<{ executionArn: string }>(`${API_BASE}/calls/${id}/replay`, {
      method: "POST",
    }),
  uploadTestAudio: async (practiceId: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    form.append("practiceId", practiceId);
    const auth = await authHeaders();
    const response = await fetch(`${API_BASE}/test/upload-audio`, {
      method: "POST",
      headers: auth, // don't set Content-Type — browser sets multipart boundary
      body: form,
    });
    if (response.status === 401) {
      onAuthExpired?.();
      throw new AuthExpiredError();
    }
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json() as Promise<{ callId: string; executionArn: string }>;
  },
};
